import { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  message,
} from 'antd';
import dayjs from 'dayjs';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { CreatePatientPayload, PatientSex } from '@/models/patient';
import { useCreatePatientMutation } from '@/state/slices/patients';
import {
  buildCreatePatientPayload,
  type NewPatientFormValues,
} from './buildCreatePatientPayload';
import styles from './NewPatientModal.module.css';

const { TextArea } = Input;

const SEX_OPTIONS: { label: string; value: PatientSex }[] = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
];

interface NewPatientModalProps {
  open: boolean;
  onClose: () => void;
}

type BackendErrorPayload = Partial<
  Record<keyof CreatePatientPayload, string[]>
>;

export default function NewPatientModal({
  open,
  onClose,
}: NewPatientModalProps) {
  const [form] = Form.useForm<NewPatientFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [createPatient, { isLoading }] = useCreatePatientMutation();

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values: NewPatientFormValues) => {
    const payload = buildCreatePatientPayload(values);

    try {
      await createPatient(payload).unwrap();
      messageApi.success('Patient created');
      form.resetFields();
      onClose();
    } catch (err) {
      const data = (err as FetchBaseQueryError | undefined)?.data as
        | BackendErrorPayload
        | undefined;
      if (data && typeof data === 'object') {
        const fieldErrors = (
          Object.keys(data) as (keyof CreatePatientPayload)[]
        )
          .filter((k) => Array.isArray(data[k]))
          .map((rawKey) => {
            const formKey: keyof NewPatientFormValues =
              rawKey === 'referringPhysician'
                ? 'referringPhysicianFirstName'
                : (rawKey as keyof NewPatientFormValues);
            return { name: formKey, errors: data[rawKey] as string[] };
          });
        if (fieldErrors.length) {
          form.setFields(fieldErrors);
          return;
        }
      }
      messageApi.error('Failed to create patient');
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="New Patient"
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        okText="Create Patient"
        okButtonProps={{ loading: isLoading }}
        cancelButtonProps={{ disabled: isLoading }}
        width={680}
        destroyOnHidden
        style={{ top: 72 }}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            paddingRight: 4,
          },
        }}
      >
        <Form<NewPatientFormValues>
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className={styles.form}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'First name is required',
                  },
                ]}
              >
                <Input placeholder="e.g. Jane" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Last name is required',
                  },
                ]}
              >
                <Input placeholder="e.g. Doe" autoComplete="off" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="MRN"
                name="medicalRecordNumber"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'MRN is required',
                  },
                ]}
              >
                <Input placeholder="e.g. 1234567" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="DOB"
                name="dateOfBirth"
                rules={[{ required: true, message: 'DOB is required' }]}
              >
                <DatePicker
                  className={styles.fullWidth}
                  format="YYYY-MM-DD"
                  placeholder="YYYY-MM-DD"
                  disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Sex" name="sex">
                <Select allowClear options={SEX_OPTIONS} placeholder="Select" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone Number" name="phoneNumber">
                <Input placeholder="e.g. (555) 123-4567" autoComplete="off" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Referring Physician First Name"
                name="referringPhysicianFirstName"
              >
                <Input placeholder="e.g. Jane" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Referring Physician Last Name"
                name="referringPhysicianLastName"
              >
                <Input placeholder="e.g. Smith" autoComplete="off" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Ethnicity" name="ethnicity">
                <Input
                  placeholder="e.g. Hispanic or Latino"
                  autoComplete="off"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input
                  placeholder="e.g. 123 Main St, Toronto, ON M5V 2A1"
                  autoComplete="off"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Notes" name="notes">
                <TextArea
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  placeholder="Add any notes about this patient…"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
